/**
 * ApprovalWorkflow-Model — the pure data layer for the config-app approval-workflow editor.
 *
 * There are no pict / DOM / REST dependencies here on purpose: everything is a pure transform over the
 * Configuration-blob shape the editor reads and writes, so it is unit-testable in isolation and reused
 * verbatim by the provider (persistence) and the editor view (rendering).
 *
 * The blob shape (NamespaceKey 'ApprovalWorkflow', one per document/observation type) is documented in
 * api/approvals/docs/WorkflowConfig.md. The editor only understands part of it, so the working model IS the
 * stored blob: every field it has no control for (Branches, Triggers, Criteria, BackPropagateTo, actor Tags, ...)
 * round-trips untouched, and every edit changes only the fields it names.
 *
 * A `Status` is a state's identity; `ToStatus` on a transition references some state's `Status`.
 */

const libRules = require('@paviasystems/approval-workflow-rules');

// The boolean transition flags the editor has checkboxes for. Absent means false to the approvals engine, so a flag
// is only written when its meaning changes; an explicit false in the stored blob stays until someone turns it on.
const TOGGLE_FLAGS = [ 'LockEntity', 'UnlockEntity', 'IsApproval', 'AllowOnLocked' ];
const COMMON_ACTOR_TAGS = [ 'Primary', 'Secondary', 'Backup', 'Override' ];

// The seed for a brand-new workflow: Draft → Submitted → { Rejected, Approved }, Rejected → Draft. Submit locks the
// document, so the actions out of Submitted need AllowOnLocked or the engine refuses them.
const NEW_DEFAULT_WORKFLOW_CONFIG =
{
	States:
	[
		{
			Status: 'Draft', StateName: 'Draft',
			Transitions:
			[
				{ Action: 'Submit', ToStatus: 'Submitted', Actors: [ 'CreatingUser' ], LockEntity: true },
			],
		},
		{
			Status: 'Submitted', StateName: 'Submitted',
			Transitions:
			[
				{ Action: 'Approve', ToStatus: 'Approved', Actors: [ 'Admin' ], IsApproval: true, AllowOnLocked: true },
				{ Action: 'Reject', ToStatus: 'Rejected', Actors: [ 'Admin' ], UnlockEntity: true, AllowOnLocked: true },
			],
		},
		{
			Status: 'Rejected', StateName: 'Rejected',
			Transitions:
			[
				{ Action: 'Revise', ToStatus: 'Draft', Actors: [ 'CreatingUser' ], UnlockEntity: true },
			],
		},
		{
			Status: 'Approved', StateName: 'Approved',
			Transitions: [],
		},
	],
};


// --- State coloring (Home's computeColor, verbatim thresholds) ---------------------------------------

/**
 * The fill color for a state node, keyed off substrings of its Status (Home computeColor).
 * @param {String} pStatus
 * @return {String} hex color
 */
function stateColor(pStatus)
{
	const tmpStatus = String(pStatus || '').toLowerCase();
	if (tmpStatus.includes('appr') || tmpStatus.includes('review'))
	{
		return '#449606';
	}
	if (tmpStatus.includes('submit') || tmpStatus.includes('pend'))
	{
		return '#2196F3';
	}
	if (tmpStatus.includes('reject'))
	{
		return '#D84028';
	}
	return '#6F727A';
}

/**
 * A light tint of a state's color, used as the state node's body fill so states are visibly color-coded
 * (not just a title chip). Keyed off the same computeColor buckets.
 */
function stateTint(pStatus)
{
	switch (stateColor(pStatus))
	{
		case '#449606': return '#eef7e6'; // approved / review — green
		case '#2196F3': return '#e6f2fd'; // submitted / pending — blue
		case '#D84028': return '#fbeae6'; // rejected — red
		default: return '#eef0f2';        // other — grey
	}
}

// --- Blob load / save / edit --------------------------------------------------------------------------

/**
 * The working model for a freshly-read blob: a deep copy, with States guaranteed to be an array.
 * @param {Object} pBlob
 * @return {Object}
 */
function normalizeLoadedBlob(pBlob)
{
	const tmpWorkflow = JSON.parse(JSON.stringify(pBlob || {}));
	if (!Array.isArray(tmpWorkflow.States))
	{
		tmpWorkflow.States = [];
	}
	return tmpWorkflow;
}

/**
 * The body to persist for a working model: a deep copy, so nothing later mutates what was sent.
 * @param {Object} pWorkflow
 * @return {Object}
 */
function prepareBlobForSave(pWorkflow)
{
	return normalizeLoadedBlob(pWorkflow);
}

/**
 * Apply the transition panel's values, touching only what actually changed. Lock and Unlock are exclusive when one
 * is newly turned on.
 * @param {Object} pTransition - Mutated.
 * @param {{ Action?: String, ToStatus?: String, LockEntity?: Boolean, UnlockEntity?: Boolean, IsApproval?: Boolean, AllowOnLocked?: Boolean }} pPatch
 * @return {Boolean} true if anything changed
 */
function applyTransitionPatch(pTransition, pPatch)
{
	let tmpChanged = false;
	[ 'Action', 'ToStatus' ].forEach((pField) =>
	{
		if ((pPatch[pField] !== undefined) && (String(pTransition[pField] == null ? '' : pTransition[pField]) !== pPatch[pField]))
		{
			pTransition[pField] = pPatch[pField];
			tmpChanged = true;
		}
	});
	const tmpNewlyOn = {};
	TOGGLE_FLAGS.forEach((pFlag) =>
	{
		if ((pPatch[pFlag] !== undefined) && (!!pPatch[pFlag] !== !!pTransition[pFlag]))
		{
			if (pPatch[pFlag])
			{
				pTransition[pFlag] = true;
				tmpNewlyOn[pFlag] = true;
			}
			else
			{
				delete pTransition[pFlag];
			}
			tmpChanged = true;
		}
	});
	if (tmpNewlyOn.LockEntity)
	{
		delete pTransition.UnlockEntity;
	}
	if (tmpNewlyOn.UnlockEntity)
	{
		delete pTransition.LockEntity;
	}
	return tmpChanged;
}

/**
 * Call a function on a transition and on every branch under it, at any depth.
 * @param {Object} pEdge
 * @param {(pEdge: Object) => void} fVisit
 */
function _visitEdgeTree(pEdge, fVisit)
{
	fVisit(pEdge);
	if (Array.isArray(pEdge.Branches))
	{
		pEdge.Branches.forEach((pBranch) => _visitEdgeTree(pBranch, fVisit));
	}
}

/**
 * Renaming a State's Status rewrites everything that references it: ToStatus on every transition and nested branch,
 * and BackPropagateTo. Mutates + returns the workflow.
 * @param {Object} pWorkflow
 * @param {String} pOldStatus
 * @param {String} pNewStatus
 */
function renameStatusCascade(pWorkflow, pOldStatus, pNewStatus)
{
	if (pOldStatus === pNewStatus)
	{
		return pWorkflow;
	}
	for (const tmpState of (pWorkflow.States || []))
	{
		for (const tmpTransition of (tmpState.Transitions || []))
		{
			_visitEdgeTree(tmpTransition, (pEdge) =>
			{
				if (pEdge.ToStatus === pOldStatus)
				{
					pEdge.ToStatus = pNewStatus;
				}
				if (Array.isArray(pEdge.BackPropagateTo))
				{
					pEdge.BackPropagateTo = pEdge.BackPropagateTo.map((pStatus) => ((pStatus === pOldStatus) ? pNewStatus : pStatus));
				}
			});
		}
	}
	return pWorkflow;
}

/**
 * Remove a state and everything that references it: transitions and branches (at any depth) that go to it, and its
 * entries in BackPropagateTo. Mutates + returns the workflow.
 * @param {Object} pWorkflow
 * @param {String} pStatus
 */
function removeStateCascade(pWorkflow, pStatus)
{
	const tmpPruneBranches = (pEdge) =>
	{
		if (Array.isArray(pEdge.Branches))
		{
			pEdge.Branches = pEdge.Branches.filter((pBranch) => pBranch.ToStatus !== pStatus);
			pEdge.Branches.forEach(tmpPruneBranches);
		}
		if (Array.isArray(pEdge.BackPropagateTo))
		{
			pEdge.BackPropagateTo = pEdge.BackPropagateTo.filter((pTarget) => pTarget !== pStatus);
		}
	};
	pWorkflow.States = (pWorkflow.States || []).filter((pState) => pState.Status !== pStatus);
	pWorkflow.States.forEach((pState) =>
	{
		if (Array.isArray(pState.Transitions))
		{
			pState.Transitions = pState.Transitions.filter((pTransition) => pTransition.ToStatus !== pStatus);
			pState.Transitions.forEach(tmpPruneBranches);
		}
	});
	return pWorkflow;
}

/**
 * Why a working model must not be written.
 * @param {Object} pWorkflow
 * @return {Array<String>} empty when it can be saved
 */
function findSaveProblems(pWorkflow)
{
	return libRules.checkWorkflow(pWorkflow).filter((pFinding) => pFinding.Severity === 'error').map((pFinding) => pFinding.Message);
}

/**
 * The states in DIAGRAM order — the top-to-bottom flow the graphviz layout reads — as an array of indices
 * into pWorkflow.States. States are layered by shortest distance from a start state (one nothing transitions
 * into); a fully-cyclic or disconnected component is seeded from its lowest-index state, and ties within a
 * layer keep the existing array position. Pure; the editor's steps rail uses this so the list reads in the
 * same order as the diagram instead of the arbitrary stored order.
 * @param {Object} pWorkflow
 * @return {Array<Number>} original state indices, in diagram order
 */
function flowOrder(pWorkflow)
{
	const tmpStates = (pWorkflow && Array.isArray(pWorkflow.States)) ? pWorkflow.States : [];
	if (!tmpStates.length)
	{
		return [];
	}
	const tmpIndexByStatus = {};
	tmpStates.forEach((pState, pIndex) => { tmpIndexByStatus[pState.Status] = pIndex; });
	// Distinct outgoing targets (transitions + flattened branches), first-seen order, as state indices.
	const tmpTargets = tmpStates.map((pState) =>
	{
		const tmpSeen = {};
		const tmpOut = [];
		const tmpConsider = (pToStatus) =>
		{
			if (tmpIndexByStatus.hasOwnProperty(pToStatus) && !tmpSeen[pToStatus])
			{
				tmpSeen[pToStatus] = true;
				tmpOut.push(tmpIndexByStatus[pToStatus]);
			}
		};
		(pState.Transitions || []).forEach((pTransition) =>
		{
			tmpConsider(pTransition.ToStatus);
			_collectBranchEdges(pTransition.Branches, []).forEach((pBranchEdge) => tmpConsider(pBranchEdge.ToStatus));
		});
		return tmpOut;
	});
	const tmpInDegree = tmpStates.map(() => 0);
	tmpTargets.forEach((pOut) => pOut.forEach((pTargetIndex) => { tmpInDegree[pTargetIndex]++; }));
	const tmpRank = tmpStates.map(() => -1);
	const tmpQueue = [];
	let tmpHead = 0;
	const tmpDrain = () =>
	{
		while (tmpHead < tmpQueue.length)
		{
			const tmpIndex = tmpQueue[tmpHead++];
			tmpTargets[tmpIndex].forEach((pTargetIndex) =>
			{
				if (tmpRank[pTargetIndex] < 0)
				{
					tmpRank[pTargetIndex] = tmpRank[tmpIndex] + 1;
					tmpQueue.push(pTargetIndex);
				}
			});
		}
	};
	// BFS from every start state (in-degree 0)…
	tmpStates.forEach((pState, pIndex) => { if (tmpInDegree[pIndex] === 0) { tmpRank[pIndex] = 0; tmpQueue.push(pIndex); } });
	tmpDrain();
	// …then seed any leftover (fully-cyclic / disconnected) component from its lowest-index state.
	for (let tmpI = 0; tmpI < tmpStates.length; tmpI++)
	{
		if (tmpRank[tmpI] < 0) { tmpRank[tmpI] = 0; tmpQueue.push(tmpI); tmpDrain(); }
	}
	return tmpStates.map((pState, pIndex) => pIndex).sort((pA, pB) => (tmpRank[pA] - tmpRank[pB]) || (pA - pB));
}

// --- Sentence generation (ActorSpec.computeActors + the transition template, verbatim) ---------------

/**
 * Collect the role PermissionLevels and user IDs referenced by every actor across the workflow, so the
 * provider can pre-resolve their display names in ONE pass before the (synchronous) node bodies render.
 * @param {Object} pWorkflow
 * @return {{ RoleLevels: Array<String>, UserIDs: Array<String> }}
 */
function collectActorReferences(pWorkflow)
{
	const tmpRoleLevels = new Set();
	const tmpUserIDs = new Set();
	for (const tmpState of (pWorkflow.States || []))
	{
		for (const tmpTransition of (tmpState.Transitions || []))
		{
			for (const tmpActor of (tmpTransition.Actors || []))
			{
				const tmpSpec = _readActorSpec(tmpActor);
				if (tmpSpec.Type === 'Users')
				{
					tmpSpec.Settings.forEach((pID) => tmpUserIDs.add(String(pID)));
				}
				else if (ROLE_ACTOR_TYPES.includes(tmpSpec.Type))
				{
					tmpSpec.Settings.forEach((pLevel) => tmpRoleLevels.add(String(pLevel)));
				}
				else if (!KEYWORD_ACTOR_TYPES.includes(tmpSpec.Type))
				{
					// Legacy "bare permission-level" actor (the default branch) — the type IS the level.
					if (tmpSpec.Type)
					{
						tmpRoleLevels.add(String(tmpSpec.Type));
					}
				}
			}
		}
	}
	return { RoleLevels: Array.from(tmpRoleLevels), UserIDs: Array.from(tmpUserIDs) };
}

const ROLE_ACTOR_TYPES = libRules.ROLE_ACTOR_TYPES;
const KEYWORD_ACTOR_TYPES = libRules.KEYWORD_ACTOR_TYPES;
const _readActorSpec = libRules.readActorSpec;

/**
 * One row per person-selector in a transition's actor list, so an editor can show and remove one role or user at a
 * time while the stored actors keep their shape, order and tags.
 * @param {Array<any>} pActors
 * @param {Object} pNames
 * @return {Array<{ ActorIndex: Number, SettingIndex: Number, Label: String, Tags: Array<String> }>} SettingIndex -1 means the whole actor
 */
function actorRows(pActors, pNames)
{
	const tmpRows = [];
	(Array.isArray(pActors) ? pActors : []).forEach((pActor, pActorIndex) =>
	{
		const tmpSpec = _readActorSpec(pActor);
		if (tmpSpec.Settings.length < 2)
		{
			tmpRows.push({ ActorIndex: pActorIndex, SettingIndex: -1, Label: actorFragment(pActor, pNames), Tags: tmpSpec.Tags });
			return;
		}
		tmpSpec.Settings.forEach((pSetting, pSettingIndex) =>
		{
			tmpRows.push({ ActorIndex: pActorIndex, SettingIndex: pSettingIndex, Label: actorFragment({ Type: tmpSpec.Type, Hashes: [ pSetting ], Tags: tmpSpec.Tags }, pNames), Tags: tmpSpec.Tags });
		});
	});
	return tmpRows;
}

/**
 * Remove one row (see actorRows) from an actor list. Other settings on the actor stay in its own form; an actor left
 * with no settings is removed. Mutates pActors.
 * @param {Array<any>} pActors
 * @param {Number} pActorIndex
 * @param {Number} pSettingIndex - -1 removes the whole actor.
 */
function removeActorRow(pActors, pActorIndex, pSettingIndex)
{
	const tmpActor = pActors[pActorIndex];
	if (tmpActor === undefined)
	{
		return;
	}
	const tmpSpec = _readActorSpec(tmpActor);
	if ((pSettingIndex < 0) || (tmpSpec.Settings.length < 2))
	{
		pActors.splice(pActorIndex, 1);
		return;
	}
	const tmpRemaining = tmpSpec.Settings.filter((pSetting, pIndex) => pIndex !== pSettingIndex);
	if (typeof tmpActor === 'string')
	{
		pActors[pActorIndex] = `${tmpSpec.Type}:${tmpRemaining.join(',')}`;
	}
	else if (tmpSpec.Field === 'Settings')
	{
		tmpActor.Settings = tmpRemaining.join(',');
	}
	else
	{
		tmpActor[tmpSpec.Field] = tmpRemaining;
	}
}

/**
 * Set the tags of one row (see actorRows). A row that shares its actor with other roles or users moves to an actor of
 * its own, right after, so the others keep their tags. A string actor with tags becomes an object. Mutates pActors.
 * @param {Array<any>} pActors
 * @param {Number} pActorIndex
 * @param {Number} pSettingIndex - -1 means the whole actor.
 * @param {Array<String>} pTags
 * @return {Boolean} true if anything changed
 */
function setActorRowTags(pActors, pActorIndex, pSettingIndex, pTags)
{
	const tmpActor = pActors[pActorIndex];
	if (tmpActor === undefined)
	{
		return false;
	}
	const tmpTags = Array.from(new Set((pTags || []).map((pTag) => String(pTag).trim()).filter(Boolean)));
	const tmpSpec = _readActorSpec(tmpActor);
	if ((tmpSpec.Tags.length === tmpTags.length) && tmpTags.every((pTag) => tmpSpec.Tags.includes(pTag)))
	{
		return false;
	}
	if ((pSettingIndex >= 0) && (tmpSpec.Settings.length > 1))
	{
		const tmpSetting = tmpSpec.Settings[pSettingIndex];
		const tmpField = ((tmpSpec.Field === 'IDs') || (tmpSpec.Field === 'Values')) ? tmpSpec.Field : 'Hashes';
		removeActorRow(pActors, pActorIndex, pSettingIndex);
		pActors.splice(pActorIndex + 1, 0, { Type: tmpSpec.Type, [tmpField]: [ tmpSetting ], Tags: tmpTags });
		return true;
	}
	if (typeof tmpActor === 'string')
	{
		pActors[pActorIndex] = tmpSpec.Settings.length ? { Type: tmpSpec.Type, Hashes: tmpSpec.Settings, Tags: tmpTags } : { Type: tmpSpec.Type, Tags: tmpTags };
		return true;
	}
	tmpActor.Tags = tmpTags;
	return true;
}

/**
 * The tags to suggest: the usual ones, then any others this workflow uses.
 * @param {Object} pWorkflow
 * @return {Array<String>}
 */
function actorTagOptions(pWorkflow)
{
	const tmpTags = new Set(COMMON_ACTOR_TAGS);
	for (const tmpState of ((pWorkflow && pWorkflow.States) || []))
	{
		for (const tmpTransition of (tmpState.Transitions || []))
		{
			(Array.isArray(tmpTransition.Actors) ? tmpTransition.Actors : []).forEach((pActor) => _readActorSpec(pActor).Tags.forEach((pTag) => tmpTags.add(pTag)));
		}
	}
	return Array.from(tmpTags);
}

/**
 * Read a comma-separated tag list. A tag that differs from a known one only by case takes the known spelling, because
 * the services match tags exactly.
 * @param {String} pText
 * @param {Array<String>} pKnown
 * @return {Array<String>}
 */
function parseActorTags(pText, pKnown)
{
	return Array.from(new Set(String(pText || '').split(',').map((pTag) => pTag.trim()).filter(Boolean).map((pTag) =>
	{
		return (pKnown || []).find((pOption) => pOption.toLowerCase() === pTag.toLowerCase()) || pTag;
	})));
}

/**
 * Add an actor. A new role/user joins an existing untagged actor of the same Type rather than repeating it; an exact
 * repeat is ignored. Mutates pActors.
 * @param {Array<any>} pActors
 * @param {any} pActor - An object for role/user actors, a string for keyword/level actors.
 */
function addActorSpec(pActors, pActor)
{
	if (typeof pActor === 'string')
	{
		if (!pActors.includes(pActor))
		{
			pActors.push(pActor);
		}
		return;
	}
	const tmpExisting = pActors.find((pOther) => pOther && (typeof pOther === 'object') && (pOther.Type === pActor.Type) && Array.isArray(pOther.Hashes) && !(pOther.Tags && pOther.Tags.length));
	if (!tmpExisting || (pActor.Tags && pActor.Tags.length))
	{
		pActors.push(pActor);
		return;
	}
	pActor.Hashes.forEach((pHash) =>
	{
		if (!tmpExisting.Hashes.includes(pHash))
		{
			tmpExisting.Hashes.push(pHash);
		}
	});
}

/**
 * The human-readable fragment for a single actor. `pNames` pre-resolves display strings:
 *   { RoleNameByLevel: { '<level>': 'Role Name' }, UserNameByID: { '<id>': 'First Last' } }
 * Mirrors ActorSpec.computeActors' switch (including the trailing " [tag1,tag2]").
 * @return {String}
 */
function actorFragment(pActor, pNames)
{
	const tmpNames = pNames || {};
	const tmpRoleNames = tmpNames.RoleNameByLevel || {};
	const tmpUserNames = tmpNames.UserNameByID || {};
	const tmpSpec = _readActorSpec(pActor);
	// A code with no matching role still shows, so an actor that matches nobody is visible rather than blank.
	const tmpResolveRoles = (pLevels) => (pLevels || []).map((pLevel) => tmpRoleNames[String(pLevel)] || String(pLevel));

	let tmpFragment;
	switch (tmpSpec.Type)
	{
		case 'HigherUserLevelInProject':
			tmpFragment = 'Users in the project who are higher level than the creating user';
			break;
		case 'MinimumUserLevelInProject':
			tmpFragment = 'Users in the same project who are at least level ' + parseInt(tmpSpec.Settings[0], 10);
			break;
		case 'Admin':
			tmpFragment = 'Administrators';
			break;
		case 'CreatingUser':
			tmpFragment = 'User who created the entity to be approved';
			break;
		case 'UserRole':
			tmpFragment = 'Users with a role in ' + tmpResolveRoles(tmpSpec.Settings).join(', ');
			break;
		case 'UserRoleProjectMembership':
			tmpFragment = 'Users with a role in ' + tmpResolveRoles(tmpSpec.Settings).join(', ') + ' who are also members of the project';
			break;
		case 'FormLabRole':
			tmpFragment = 'Users in the selected lab (from the form) with a role in ' + tmpResolveRoles(tmpSpec.Settings).join(', ');
			break;
		case 'FormLabUser':
			tmpFragment = 'Users in the selected lab (from the form)';
			break;
		case 'FormMaterialLabInitialUser':
			tmpFragment = 'First-contact users for the lab and material on the form';
			break;
		case 'FormMaterialLabEscalationUser':
			tmpFragment = 'Escalation users for the lab and material on the form';
			break;
		case 'FormMaterialLabRole':
			tmpFragment = 'Users assigned to the lab and material on the form with a role in ' + tmpResolveRoles(tmpSpec.Settings).join(', ');
			break;
		case 'ProjectEntitlement':
			tmpFragment = 'Users with a project entitlement in ' + tmpResolveRoles(tmpSpec.Settings).join(', ');
			break;
		case 'ProjectRole':
		{
			const tmpRoles = tmpResolveRoles(tmpSpec.Settings);
			tmpFragment = tmpRoles.length ? ('Users with a project role in ' + tmpRoles.join(', ')) : 'Users with a nonexistent project role';
			break;
		}
		case 'Users':
		{
			const tmpUsers = (tmpSpec.Settings || []).map((pID) => tmpUserNames[String(pID)] || `Unknown user ${pID}`);
			if (!tmpUsers.length) { tmpFragment = 'Unknown User'; }
			else if (tmpUsers.length === 1) { tmpFragment = tmpUsers[0]; }
			else { tmpFragment = 'One of ' + tmpUsers.join(', '); }
			break;
		}
		default:
		{
			// Legacy bare-permission-level actor: the Type is itself a level to resolve.
			const tmpRoleName = tmpRoleNames[String(tmpSpec.Type)];
			if (tmpRoleName)
			{
				tmpFragment = 'Roles in ' + tmpRoleName;
			}
			else
			{
				tmpFragment = tmpSpec.Type || 'Unknown actor';
				if (tmpSpec.Settings && tmpSpec.Settings.length)
				{
					tmpFragment += ':' + tmpSpec.Settings.join(', ');
				}
			}
			break;
		}
	}
	if (tmpSpec.Tags && tmpSpec.Tags.length)
	{
		tmpFragment += ` [${tmpSpec.Tags.join(',')}]`;
	}
	return tmpFragment;
}

/**
 * The full actor clause: every actor fragment joined by " and " (Home renders them underlined, joined
 * with " and ").
 */
function actorsClause(pActors, pNames)
{
	const tmpActors = Array.isArray(pActors) ? pActors : [];
	if (!tmpActors.length)
	{
		return 'No one';
	}
	return tmpActors.map((pActor) => actorFragment(pActor, pNames)).join(' and ');
}

/**
 * Escape a string for safe interpolation into node-body HTML (sentences carry user-entered role names).
 */
function _escapeHTML(pValue)
{
	return String(pValue == null ? '' : pValue)
		.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

/**
 * Format a scalar criteria value for display (booleans read naturally).
 */
function _formatCriteriaValue(pValue)
{
	if (typeof pValue === 'boolean')
	{
		return pValue ? 'true' : 'false';
	}
	return String(pValue);
}

/**
 * Human-readable text for ONE branch condition object. Handles the condition Types seen in real workflow
 * data (HasProjectSetting, FormDataMetadata, …) and falls back to a Key/Value or Type readout. This is a
 * deliberate improvement over Home, which renders the raw object as "[object Object]".
 */
function criteriaConditionText(pCondition)
{
	if (pCondition == null)
	{
		return '';
	}
	if (typeof pCondition === 'string')
	{
		return pCondition;
	}
	switch (pCondition.Type)
	{
		case 'HasProjectSetting':
			return `the project setting "${pCondition.SettingKey}" is ${pCondition.Not ? 'not ' : ''}set`;
		case 'FormDataMetadata':
			return (pCondition.Value !== undefined)
				? `${pCondition.Key} is ${_formatCriteriaValue(pCondition.Value)}`
				: `${pCondition.Key} is set`;
		case 'True':
			return 'always';
		case 'False':
			return 'never';
		case 'EntityOwner':
			return `${pCondition.UserID ? `user ${pCondition.UserID}` : 'the person acting'} ${pCondition.Not ? 'does not own' : 'owns'} the document`;
		case 'ChangeOrderThreshold':
		{
			const tmpParts = [];
			if (pCondition.MinDays !== undefined)
			{
				tmpParts.push(`the schedule changes by at least ${pCondition.MinDays} days`);
			}
			if (pCondition.MinAmount !== undefined)
			{
				tmpParts.push(`the cost changes by at least ${pCondition.MinAmount}`);
			}
			if (pCondition.PercentContractChange !== undefined)
			{
				tmpParts.push(`the contract changes by at least ${pCondition.PercentContractChange}%`);
			}
			return tmpParts.length ? tmpParts.join(' or ') : 'a change order threshold is reached';
		}
		case 'DocumentHasObsWithLabel':
			return pCondition.LabelValue ? `an observation on the document has the ${pCondition.LabelType} label "${pCondition.LabelValue}"` : `an observation on the document has a ${pCondition.LabelType} label`;
		case 'RecordNotFound':
			return `no record matches ${pCondition.QueryString}`;
		default:
		{
			const tmpKey = pCondition.Key || pCondition.SettingKey || pCondition.Type || 'a condition';
			return (pCondition.Value !== undefined) ? `${tmpKey} is ${_formatCriteriaValue(pCondition.Value)}` : String(tmpKey);
		}
	}
}

/**
 * Human-readable text for a branch's Criteria, which may be an array of condition objects (the engine takes the
 * branch when ANY matches, hence " or "), a single object, or a legacy string.
 */
function criteriaText(pCriteria)
{
	if (Array.isArray(pCriteria))
	{
		const tmpParts = pCriteria.map(criteriaConditionText).filter(Boolean);
		return tmpParts.length ? tmpParts.join(' or ') : 'a condition';
	}
	if (pCriteria && typeof pCriteria === 'object')
	{
		return criteriaConditionText(pCriteria);
	}
	if (typeof pCriteria === 'string' && pCriteria)
	{
		return pCriteria;
	}
	return 'a condition';
}

/**
 * Render the branch re-route lines for a transition, recursing into nested branch.Branches (indented).
 * @return {String} HTML
 */
function _branchLinesHTML(pBranches, pStateMap, pDepth)
{
	let tmpHTML = '';
	for (const tmpBranch of (pBranches || []))
	{
		const tmpTarget = pStateMap[tmpBranch.ToStatus] || tmpBranch.ToStatus || '(unset)';
		const tmpPrefix = pDepth > 0 ? '↳ ' : '';
		tmpHTML += `<br><span class="apw-branch">${tmpPrefix}If ${_escapeHTML(criteriaText(tmpBranch.Criteria))}, instead goes to ${_escapeHTML(tmpTarget)}.</span>`;
		tmpHTML += _branchLinesHTML(tmpBranch.Branches, pStateMap, pDepth + 1);
	}
	return tmpHTML;
}

/**
 * The full transition sentence (the transition node's body). Mirrors Home's transition <p>:
 *   "{Actors} can {Action} and go to {StateName-of-ToStatus}. This locks/unlocks the entity. This is an
 *    approval. This transition is allowed on locked entities." (+ branch re-route lines).
 * `pStateMap` maps Status → StateName. Returns an HTML string (actors + target emphasized).
 * @return {String}
 */
function transitionSentence(pTransition, pStateMap, pNames)
{
	const tmpStateMap = pStateMap || {};
	const tmpTargetName = tmpStateMap[pTransition.ToStatus] || pTransition.ToStatus || '(unset)';
	const tmpActors = actorsClause(pTransition.Actors, pNames);
	const tmpAction = pTransition.Action || '(no action)';

	let tmpHTML = `<span class="apw-actors">${_escapeHTML(tmpActors)}</span> can `
		+ `<span class="apw-action">${_escapeHTML(tmpAction)}</span> and go to `
		+ `<span class="apw-target">${_escapeHTML(tmpTargetName)}.</span>`;

	if (pTransition.LockEntity)
	{
		tmpHTML += ' <b>This locks the entity.</b>';
	}
	else if (pTransition.UnlockEntity)
	{
		tmpHTML += ' <b>This unlocks the entity.</b>';
	}
	if (pTransition.IsApproval)
	{
		tmpHTML += ' <b>This is an approval.</b>';
	}
	if (pTransition.AllowOnLocked)
	{
		tmpHTML += ' <b>This transition is allowed on locked entities.</b>';
	}
	tmpHTML += _branchLinesHTML(pTransition.Branches, tmpStateMap, 0);
	return tmpHTML;
}

/**
 * Build the { Status → StateName } lookup for a workflow.
 */
function buildStateMap(pWorkflow)
{
	const tmpMap = {};
	for (const tmpState of (pWorkflow.States || []))
	{
		tmpMap[tmpState.Status] = tmpState.StateName;
	}
	return tmpMap;
}

/**
 * Flatten a transition's branches (including nested branch.Branches) into a list of conditional edges, accumulating the
 * criteria text down the nesting. Each entry: { Branch, ToStatus, CriteriaText }.
 */
function _collectBranchEdges(pBranches, pParentCriteria)
{
	const tmpEdges = [];
	for (const tmpBranch of (pBranches || []))
	{
		const tmpCriteria = pParentCriteria.concat([ criteriaText(tmpBranch.Criteria) ]).filter(Boolean);
		tmpEdges.push({ Branch: tmpBranch, ToStatus: tmpBranch.ToStatus, CriteriaText: tmpCriteria.join(' and ') });
		if (Array.isArray(tmpBranch.Branches) && tmpBranch.Branches.length)
		{
			tmpEdges.push(..._collectBranchEdges(tmpBranch.Branches, tmpCriteria));
		}
	}
	return tmpEdges;
}

/**
 * Plain-language rows for what a transition carries beyond the controls on its panel, so an admin can see it's there.
 * @param {Object} pTransition
 * @param {Object} pStateMap - Status → StateName.
 * @return {Array<{ Label: String, Detail: String }>}
 */
function otherTransitionSettings(pTransition, pStateMap)
{
	const tmpStateMap = pStateMap || {};
	const tmpNameOf = (pStatus) => tmpStateMap[pStatus] || pStatus;
	const tmpRows = [];
	const tmpHandled = new Set([ 'Action', 'ToStatus', 'Actors', ...TOGGLE_FLAGS ]);
	const tmpAdd = (pKey, pLabel, pDetail) =>
	{
		tmpHandled.add(pKey);
		if (pTransition[pKey] !== undefined)
		{
			tmpRows.push({ Label: pLabel, Detail: pDetail(pTransition[pKey]) });
		}
	};
	tmpAdd('Branches', 'Conditional routes', (pBranches) => _collectBranchEdges(pBranches, []).map((pEdge) => `If ${pEdge.CriteriaText}, goes to ${tmpNameOf(pEdge.ToStatus)}`).join('. '));
	tmpAdd('Criteria', 'Only offered when', (pCriteria) => criteriaText(pCriteria));
	tmpAdd('Triggers', 'Also does', (pTriggers) => (Array.isArray(pTriggers) ? pTriggers : []).map((pTrigger) => TRIGGER_LABELS[pTrigger && pTrigger.Event] || (pTrigger && pTrigger.Event) || 'Unknown').join(', '));
	tmpAdd('BackPropagateTo', 'Can be pre-approved from', (pStatuses) => (Array.isArray(pStatuses) ? pStatuses : []).map(tmpNameOf).join(', '));
	tmpAdd('BackPropagateCriteria', 'Pre-approval only when', (pCriteria) => criteriaText(pCriteria));
	tmpAdd('InferBackPropagateCriteria', 'Pre-approval conditions', (pValue) => (pValue ? 'Taken from the route into this state' : 'Not inferred'));
	tmpAdd('InvalidatesApproval', 'Cancels earlier approvals', (pValue) => (pValue ? 'Yes' : 'No'));
	tmpAdd('CreateLineItem', 'Creates line items', (pValue) => (pValue ? 'Yes' : 'No'));
	tmpAdd('RequireProjectSetting', 'Needs project setting', (pValue) => String(pValue));
	tmpAdd('AdditionalRequiredFields', 'Required form fields', (pFields) => (Array.isArray(pFields) ? pFields.join(', ') : String(pFields)));
	Object.keys(pTransition).filter((pKey) => !tmpHandled.has(pKey)).forEach((pKey) =>
	{
		tmpRows.push({ Label: pKey, Detail: `${JSON.stringify(pTransition[pKey])} (not read by the approvals service)` });
	});
	return tmpRows;
}

const TRIGGER_LABELS =
{
	ProjectFilePromote: 'Publishes the document as a project file',
	LockDocumentSample: 'Locks the document\'s samples',
	UnlockDocumentSample: 'Unlocks the document\'s samples',
	SetDocumentSampleStatus: 'Sets the status of the document\'s samples',
	LIMSClaim: 'Claims the document for LIMS',
};

module.exports =
{
	TOGGLE_FLAGS,
	NEW_DEFAULT_WORKFLOW_CONFIG,
	ROLE_ACTOR_TYPES,
	KEYWORD_ACTOR_TYPES,
	stateColor,
	stateTint,
	normalizeLoadedBlob,
	prepareBlobForSave,
	applyTransitionPatch,
	renameStatusCascade,
	removeStateCascade,
	findSaveProblems,
	findUnreachableStates: libRules.findUnreachableStates,
	findDanglingTargets: libRules.findDanglingTargets,
	workflowsEqual: libRules.deepEqual,
	workflowChanges: libRules.workflowChanges,
	workflowNameProblem: libRules.workflowNameProblem,
	checkWorkflow: libRules.checkWorkflow,
	actorRows,
	removeActorRow,
	setActorRowTags,
	actorTagOptions,
	parseActorTags,
	addActorSpec,
	flowOrder,
	collectActorReferences,
	actorFragment,
	actorsClause,
	criteriaText,
	criteriaConditionText,
	transitionSentence,
	buildStateMap,
	otherTransitionSettings,
};
